import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations/hooks'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { COCKPIT_AMPLIFY_MEMBERSHIP_OTHER_LABEL, OTHER_COCKPIT_AMPLIFY_MEMBERSHIP } from '../../lib'
import { ICockpitFilterBubbleItem } from '../../ui/cockpitFilterPanel/components/cockpitFilterBubbleList/CockpitFilterBubbleList'
import {
  setCockpitFilterAmplifyMemberships,
  setCockpitFilterGeoMappings,
  setCockpitFilterGlobalPartners,
  setCockpitFilterManager,
  setCockpitFilterMandatoryStatus,
  setCockpitFilterMdfStatus,
  setCockpitFilterPartnerClasses,
  setCockpitFilterPbm,
  setCockpitFilterPlannedStatus,
} from '../financialCockpitSlice'
import {
  selectCockpitFilterAmplifyMemberships,
  selectCockpitFilterGeoMappings,
  selectCockpitFilterGlobalPartners,
  selectCockpitFilterManager,
  selectCockpitFilterMandatoryStatus,
  selectCockpitFilterMdfStatus,
  selectCockpitFilterPartnerClasses,
  selectCockpitFilterPbm,
  selectCockpitFilterPlannedStatus,
  selectCockpitGeoMappingItems,
  selectCockpitGlobalPartnerOptions,
  selectCockpitManagerUsers,
  selectCockpitPartnerClassFlags,
  selectCockpitPbmUsers,
} from '../selectors'
import { NOT_PART_OF_GLOBAL_PARTNER } from '../types'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

export const useCockpitFilterOptionsBubbleItems = (): ICockpitFilterBubbleItem[] => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()

  const pbm = useSelector(selectCockpitFilterPbm)
  const pbmUsers = useSelector(selectCockpitPbmUsers)
  const manager = useSelector(selectCockpitFilterManager)
  const managerUsers = useSelector(selectCockpitManagerUsers)
  const geoMappings = useSelector(selectCockpitFilterGeoMappings)
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)
  const amplifyMemberships = useSelector(selectCockpitFilterAmplifyMemberships)
  const partnerClasses = useSelector(selectCockpitFilterPartnerClasses)
  const partnerClassFlags = useSelector(selectCockpitPartnerClassFlags)
  const mdfStatus = useSelector(selectCockpitFilterMdfStatus)
  const plannedStatus = useSelector(selectCockpitFilterPlannedStatus)
  const mandatoryStatus = useSelector(selectCockpitFilterMandatoryStatus)
  const globalPartners = useSelector(selectCockpitFilterGlobalPartners)
  const globalPartnerOptions = useSelector(selectCockpitGlobalPartnerOptions)

  return useMemo(() => {
    const items: ICockpitFilterBubbleItem[] = []

    if (pbm.length > 0) {
      const names = pbm.map(
        (email) => pbmUsers.find((user) => user.Email === email)?.FullName ?? email
      )
      items.push({
        key: 'pbm',
        label: t(KEYS.pbm.label),
        value: names.join(' / '),
        onRemove: () => dispatch(setCockpitFilterPbm([])),
      })
    }

    if (manager.length > 0) {
      const names = manager.map(
        (email) => managerUsers.find((user) => user.Email === email)?.FullName ?? email
      )
      items.push({
        key: 'manager',
        label: t(KEYS.manager.label),
        value: names.join(' / '),
        onRemove: () => dispatch(setCockpitFilterManager([])),
      })
    }

    if (geoMappings.length > 0) {
      const names = geoMappings
        .map((id) => geoMappingItems.find((item) => item.itemId === id)?.name)
        .filter((name): name is string => Boolean(name))
      items.push({
        key: 'geoMappings',
        label: t(KEYS.geoMapping.label),
        value: names.join(' / '),
        onRemove: () => dispatch(setCockpitFilterGeoMappings([])),
      })
    }

    if (amplifyMemberships.length > 0) {
      items.push({
        key: 'amplifyMemberships',
        label: t(KEYS.amplifyMembership.label),
        value: amplifyMemberships
          .map((membership) =>
            membership === OTHER_COCKPIT_AMPLIFY_MEMBERSHIP
              ? COCKPIT_AMPLIFY_MEMBERSHIP_OTHER_LABEL
              : membership
          )
          .join(' / '),
        onRemove: () => dispatch(setCockpitFilterAmplifyMemberships([])),
      })
    }

    if (partnerClasses.length > 0) {
      const titles = partnerClasses.map(
        (partnerFlag) =>
          partnerClassFlags.find((flag) => flag.PartnerFlag === partnerFlag)?.Title ?? partnerFlag
      )
      items.push({
        key: 'partnerClasses',
        label: t(KEYS.partnerClass.label),
        value: titles.join(' / '),
        onRemove: () => dispatch(setCockpitFilterPartnerClasses([])),
      })
    }

    if (mdfStatus !== 'All') {
      items.push({
        key: 'mdfStatus',
        label: t(KEYS.mdfStatus.label),
        value: mdfStatus,
        onRemove: () => dispatch(setCockpitFilterMdfStatus('All')),
      })
    }

    if (plannedStatus !== 'All') {
      items.push({
        key: 'plannedStatus',
        label: t(KEYS.plannedStatus.label),
        value: plannedStatus,
        onRemove: () => dispatch(setCockpitFilterPlannedStatus('All')),
      })
    }

    if (mandatoryStatus !== 'All') {
      items.push({
        key: 'mandatoryStatus',
        label: t(KEYS.mandatoryStatus.label),
        value: t(KEYS.mandatoryStatus.options[mandatoryStatus]),
        onRemove: () => dispatch(setCockpitFilterMandatoryStatus('All')),
      })
    }

    if (globalPartners.length > 0) {
      const names = globalPartners.map((globalPartner) =>
        globalPartner === NOT_PART_OF_GLOBAL_PARTNER
          ? t(KEYS.globalPartner.notPartOfGlobalPartner)
          : (globalPartnerOptions.find((option) => option.GlobalPartnerId === globalPartner)
              ?.GlobalPartnerName ?? globalPartner)
      )
      items.push({
        key: 'globalPartners',
        label: t(KEYS.globalPartner.label),
        value: names.join(' / '),
        onRemove: () => dispatch(setCockpitFilterGlobalPartners([])),
      })
    }

    return items
  }, [
    pbm,
    pbmUsers,
    manager,
    managerUsers,
    geoMappings,
    geoMappingItems,
    amplifyMemberships,
    partnerClasses,
    partnerClassFlags,
    mdfStatus,
    plannedStatus,
    mandatoryStatus,
    globalPartners,
    globalPartnerOptions,
    dispatch,
    t,
  ])
}
